import * as Sentry from '@sentry/react';

export type SupportTicket = {
  fullName: string;
  email: string;
  subject: string;
  description: string;
  file?: File | null;
  /** Chat so far, appended to the description when the user chose to include it. */
  transcript?: string;
};

/** HubSpot text fields have a max length of 65,535 characters (2^16 - 1) */
const HUBSPOT_MAX_TEXT_LENGTH = 65_535;
const MAX_TRANSCRIPT_LENGTH = 50_000;

const withTranscript = (description: string, transcript: string): string => {
  const truncated =
    transcript.length > MAX_TRANSCRIPT_LENGTH
      ? `${transcript.slice(0, MAX_TRANSCRIPT_LENGTH)}\n[Transcript truncated]`
      : transcript;
  return `${description}\n\n--- Chat Transcript ---\n${truncated}\n--- End Transcript ---`;
};

const clampToHubSpotLimit = (description: string): string => {
  if (description.length <= HUBSPOT_MAX_TEXT_LENGTH) {
    return description;
  }
  const suffix = '\n[Content truncated]';
  return `${description.slice(0, HUBSPOT_MAX_TEXT_LENGTH - suffix.length)}${suffix}`;
};

/** Posts a support ticket to the HubSpot form that feeds the support queue. */
export const submitSupportTicket = async ({
  fullName,
  email,
  subject,
  description,
  file,
  transcript,
}: SupportTicket): Promise<void> => {
  const PORTAL_ID = import.meta.env.VITE_APP_HUBSPOT_PORTAL_ID;
  const FORM_GUID = import.meta.env.VITE_APP_HUBSPOT_FORM_GUID;

  if (!(PORTAL_ID && FORM_GUID)) {
    throw new Error('HubSpot configuration missing');
  }

  const content = clampToHubSpotLimit(transcript ? withTranscript(description, transcript) : description);

  const trimmedName = fullName.trim();
  const spaceIndex = trimmedName.indexOf(' ');
  const firstName = spaceIndex > -1 ? trimmedName.slice(0, spaceIndex) : trimmedName;
  const lastName = spaceIndex > -1 ? trimmedName.slice(spaceIndex + 1).trim() : '-';

  const formData = new FormData();
  formData.append('email', email);
  formData.append('firstname', firstName);
  formData.append('lastname', lastName);
  formData.append('TICKET.subject', subject);
  formData.append('TICKET.content', content);
  formData.append('TICKET.issue_type_main', 'Dashboard');

  if (file) {
    formData.append('TICKET.hs_file_upload', file, file.name);
  }

  formData.append(
    'hs_context',
    JSON.stringify({
      pageUri: window.location.href,
      pageName: 'Dashboard',
    }),
  );

  const userFacingError = new Error('Unable to submit ticket. Please try again.');

  let response: Response;
  try {
    response = await fetch(`https://forms.hubspot.com/uploads/form/v2/${PORTAL_ID}/${FORM_GUID}`, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    throw userFacingError;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => 'Unknown error');
    Sentry.captureException(new Error(`HubSpot submission failed: ${response.status} - ${errorBody}`));
    throw userFacingError;
  }
};
